import React, { useState } from 'react';
import { Panel } from 'primereact/panel';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ScrollPanel } from 'primereact/scrollpanel';

interface WorkflowStepDetailsProps {
  stepName: string;
  stepStatus: 'completed' | 'running' | 'pending' | 'failed';
  stepInput?: any;
  stepOutput?: any;
  stepLogs?: string[];
  stepMetrics?: {
    duration?: number;
    memory?: number;
    apiCalls?: number;
  };
  stepRetryHistory?: Array<{
    attempt: number;
    timestamp: string;
    status: string;
    error?: string;
  }>;
  onRetryStep?: () => void;
  onSkipStep?: () => void;
}

const WorkflowStepDetails: React.FC<WorkflowStepDetailsProps> = ({
  stepName,
  stepStatus,
  stepInput,
  stepOutput,
  stepLogs = [],
  stepMetrics,
  stepRetryHistory = [],
  onRetryStep,
  onSkipStep
}) => {
  const [logFilter, setLogFilter] = useState('');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#22C55E';
      case 'running':
        return '#3B82F6';
      case 'failed':
        return '#EF4444';
      default:
        return '#9CA3AF';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return 'pi pi-check-circle';
      case 'running':
        return 'pi pi-spin pi-spinner';
      case 'failed':
        return 'pi pi-times-circle';
      default:
        return 'pi pi-circle';
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatMemory = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const filteredLogs = stepLogs.filter(log =>
    log.toLowerCase().includes(logFilter.toLowerCase())
  );

  return (
    <Panel
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <i
            className={getStatusIcon(stepStatus)}
            style={{ color: getStatusColor(stepStatus), fontSize: '1.25rem' }}
          ></i>
          <span style={{ fontWeight: 600, fontSize: '1rem' }}>{stepName}</span>
          <Tag
            value={stepStatus.charAt(0).toUpperCase() + stepStatus.slice(1)}
            severity={
              stepStatus === 'completed' ? 'success' :
              stepStatus === 'running' ? 'info' :
              stepStatus === 'failed' ? 'danger' : 'secondary'
            }
            style={{ fontSize: '0.75rem' }}
          />
        </div>
      }
      toggleable
      collapsed={stepStatus === 'pending'}
    >
      <TabView>
        {/* Overview Tab */}
        <TabPanel header="Overview" leftIcon="pi pi-info-circle">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {stepMetrics && (
              <>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Duration
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1f2937' }}>
                    {formatDuration(stepMetrics.duration)}
                  </div>
                </div>

                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Memory Used
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1f2937' }}>
                    {formatMemory(stepMetrics.memory)}
                  </div>
                </div>

                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    API Calls
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1f2937' }}>
                    {stepMetrics.apiCalls || 0}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Step Actions */}
          {(stepStatus === 'failed' || stepStatus === 'running') && (
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
              {onRetryStep && stepStatus === 'failed' && (
                <Button
                  label="Retry Step"
                  icon="pi pi-refresh"
                  size="small"
                  severity="warning"
                  onClick={onRetryStep}
                />
              )}
              {onSkipStep && stepStatus === 'failed' && (
                <Button
                  label="Skip Step"
                  icon="pi pi-forward"
                  size="small"
                  text
                  onClick={onSkipStep}
                />
              )}
            </div>
          )}
        </TabPanel>

        {/* Input Tab */}
        <TabPanel header="Input" leftIcon="pi pi-sign-in">
          <ScrollPanel style={{ width: '100%', height: '300px' }}>
            <pre style={{
              backgroundColor: '#1f2937',
              color: '#e5e7eb',
              padding: '1rem',
              borderRadius: '6px',
              fontSize: '0.75rem',
              lineHeight: 1.6,
              margin: 0,
              fontFamily: 'monospace'
            }}>
              {stepInput ? JSON.stringify(stepInput, null, 2) : 'No input data available'}
            </pre>
          </ScrollPanel>
        </TabPanel>

        {/* Output Tab */}
        <TabPanel header="Output" leftIcon="pi pi-sign-out">
          <ScrollPanel style={{ width: '100%', height: '300px' }}>
            <pre style={{
              backgroundColor: '#1f2937',
              color: '#e5e7eb',
              padding: '1rem',
              borderRadius: '6px',
              fontSize: '0.75rem',
              lineHeight: 1.6,
              margin: 0,
              fontFamily: 'monospace'
            }}>
              {stepOutput ? JSON.stringify(stepOutput, null, 2) : 'No output data available yet'}
            </pre>
          </ScrollPanel>
        </TabPanel>

        {/* Logs Tab */}
        <TabPanel header="Logs" leftIcon="pi pi-list">
          <div style={{ marginBottom: '1rem' }}>
            <span className="p-input-icon-left" style={{ width: '100%' }}>
              <i className="pi pi-search" />
              <InputText
                placeholder="Filter logs..."
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value)}
                style={{ width: '100%' }}
              />
            </span>
          </div>
          <ScrollPanel style={{ width: '100%', height: '300px' }}>
            <div style={{
              backgroundColor: '#1f2937',
              padding: '1rem',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              lineHeight: 1.6
            }}>
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log, index) => (
                  <div
                    key={index}
                    style={{
                      color: log.includes('ERROR') ? '#ef4444' :
                             log.includes('WARN') ? '#f59e0b' :
                             log.includes('INFO') ? '#3b82f6' : '#e5e7eb',
                      marginBottom: '0.25rem'
                    }}
                  >
                    {log}
                  </div>
                ))
              ) : (
                <div style={{ color: '#9ca3af' }}>
                  {stepLogs.length === 0 ? 'No logs available' : 'No logs match your filter'}
                </div>
              )}
            </div>
          </ScrollPanel>
        </TabPanel>

        {/* Retry History Tab */}
        {stepRetryHistory.length > 0 && (
          <TabPanel header="Retry History" leftIcon="pi pi-history">
            <DataTable value={stepRetryHistory} className="p-datatable-sm">
              <Column field="attempt" header="Attempt" style={{ width: '100px' }} />
              <Column
                field="timestamp"
                header="Timestamp"
                body={(row) => new Date(row.timestamp).toLocaleString()}
              />
              <Column
                field="status"
                header="Status"
                body={(row) => (
                  <Tag
                    value={row.status}
                    severity={row.status === 'success' ? 'success' : 'danger'}
                  />
                )}
                style={{ width: '120px' }}
              />
              <Column
                field="error"
                header="Error"
                body={(row) => row.error || '-'}
              />
            </DataTable>
          </TabPanel>
        )}
      </TabView>
    </Panel>
  );
};

export default WorkflowStepDetails;
